import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { CssBaseline } from "@mui/material";
import TopBar from "./components/layout/TopBar";
import Footer from "./components/layout/Footer";
import SideMenu from "./components/navigation/SideMenu";
import UniversalContentRenderer from "./components/content/UniversalContentRenderer";

const MainApp = ({ basePath }) => {
  const { pathname } = useLocation();
  const language = useSelector((state) => state.language.activeLanguage);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [menuFileRef, setMenuFileRef] = useState(null);
  const [navigationSections, setNavigationSections] = useState(null);
  const [navigationLoading, setNavigationLoading] = useState(true);
  const prevPathnameRef = useRef(pathname);
  const contentScrollRef = useRef(null);

  const getResourceUrl = useCallback((path) => {
    if (path.startsWith('/')) {
      return '.' + path;
    }
    return path;
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const fullUri = useMemo(() => {
    return pathname.startsWith("/") ? pathname.substring(1) : pathname;
  }, [pathname]);

  useEffect(() => {
    const loadNavigation = async () => {
      setNavigationLoading(true);
      try {
        const response = await fetch(`./config/${language}/navigation.json`);
        if (response.ok) {
          const data = await response.json();
          setNavigationSections(data.sections);
          setNavigationLoading(false);
          return data.sections;
        }
      } catch (error) {
        console.error("Failed to load navigation:", error);
        setNavigationLoading(false);
      }
      return null;
    };

    loadNavigation().then((sections) => {
      if (sections) {
        const currentSection = sections.find((section) =>
          pathname.startsWith(section.path),
        );
        const newMenuFile = currentSection?.menuFile || sections[0]?.menuFile;

        if (newMenuFile !== menuFileRef) {
          setMenuFileRef(newMenuFile);
        }
      }
    });
  }, [language, pathname, menuFileRef]);

  useEffect(() => {
    if (!menuFileRef) return;

    const loadMenu = async () => {
      try {
        const response = await fetch(`./config/${language}/${menuFileRef}`);
        if (response.ok) {
          const menuData = await response.json();
          setCurrentMenu(menuData);
        }
      } catch (error) {
        console.error("Failed to load menu:", error);
      }
    };

    loadMenu();
  }, [menuFileRef, language]);

  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      closeMobileMenu();
      prevPathnameRef.current = pathname;
      if (contentScrollRef.current) {
        contentScrollRef.current.scrollTo({ top: 0 });
      } else {
        window.scrollTo({ top: 0 });
      }
    }
  }, [pathname, closeMobileMenu]);

  return (
    <div className="main-app">
      <CssBaseline />

      <TopBar
        pathname={pathname}
        mobileMenuOpen={mobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        navigationSections={navigationSections}
        navigationLoading={navigationLoading}
      />

      <main className="main-content">
        {currentMenu && (
          <SideMenu
            menu={currentMenu}
            pathname={pathname}
            mobileMenuOpen={mobileMenuOpen}
            closeMobileMenu={closeMobileMenu}
            navigationSections={navigationSections}
            navigationLoading={navigationLoading}
            basePath={basePath}
          />
        )}

        <div
          ref={contentScrollRef}
          className="content-container"
        >
          <div
            key={fullUri}
            className="content-wrapper"
          >
            <UniversalContentRenderer
              uri={fullUri}
              menuFileRef={menuFileRef}
              menu={currentMenu}
              scrollContainerRefProp={contentScrollRef}
              basePath={basePath}
              getResourceUrl={getResourceUrl}
            />
          </div>

          <Footer />
        </div>
      </main>
    </div>
  );
};

export default MainApp;